// RoleGate
// --------
// Per-role route guard. PrivateRoute already gates on authentication; this
// gates on the specific Cognito group.
//
// Behavior:
//   - User hydrated AND not in an admin role  → redirect to Dashboard.
//   - User not yet hydrated (role === [])     → render <Outlet/>. The wrapped
//                                                page is responsible for its
//                                                own loading state; no
//                                                tenant data flashes because
//                                                tenant data is fetched per
//                                                page and is itself authed.
//   - User hydrated AND admin/owner           → render <Outlet/>.
//
// Defense-in-depth: the backend remains the authoritative authorization
// boundary. This guard exists to prevent a Pentester from URL-typing an
// admin-only page (e.g. /manageusers, /addapplication, /integrations) and
// reaching it via the deep-link path. It does NOT replace server-side
// resolver checks.
//
// Why not use PrivateRoute with a `requireAdmin` flag: keeping the two
// guards distinct makes the intent obvious in AppRoutes.tsx — a route
// wrapped in <PrivateRoute><RoleGate>...</RoleGate></PrivateRoute> reads
// "must be authenticated AND must be admin", which is the contract we
// want enforced.

import { Navigate, Outlet } from "react-router-dom";
import { DASHBOARD_PAGE } from "../constants/definitions";
import UserClass from "../models/user";

interface RoleGateProps {
  user: UserClass;
}

export function RoleGate({ user }: RoleGateProps) {
  // Mid-hydration: role is empty until getUser() resolves. Render outlet
  // and let the page show its own loading state — the page's data fetch
  // is independently authed at the resolver, so no tenant data leaks.
  const userIsHydrated = user.role.length > 0;
  if (userIsHydrated && !user.isUserAdmin()) {
    return <Navigate to={DASHBOARD_PAGE} replace />;
  }
  return <Outlet />;
}

export default RoleGate;
